import React, { useState, useEffect } from 'react';
import { doc, updateDoc, Timestamp, collection, query, where, onSnapshot, getDocs } from 'firebase/firestore';
import { appId, db } from '../../App';

const formatDate = (date) => {
    if (!date) return 'N/A';
    const d = date instanceof Timestamp ? date.toDate() : new Date(date);
    if (isNaN(d.getTime())) return 'Fecha Inválida';
    return d.toLocaleDateString('es-GT', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

function ValidacionesModule({ showModal, permissions }) {
    const [packages, setPackages] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedYear, setSelectedYear] = useState('');
    const [selectedMonth, setSelectedMonth] = useState('');
    const [errorReport, setErrorReport] = useState([]);
    const [duplicateReport, setDuplicateReport] = useState([]);
    const [activeReport, setActiveReport] = useState(null);
    
    // --- CAMBIO #1: Estados para los filtros dinámicos ---
    const [availableYears, setAvailableYears] = useState([]);
    const [availableMonths, setAvailableMonths] = useState({});

    const canResolveErrors = permissions?.VALIDACIONES?.resolve;

    // --- CAMBIO #2: Efecto para cargar las opciones de los filtros (Años y Meses) ---
    useEffect(() => {
        const fetchAvailableDates = async () => {
            const packagesCollectionPath = `artifacts/${appId}/packages`;
            const q = query(collection(db, packagesCollectionPath));
            const querySnapshot = await getDocs(q);

            const yearMonthMap = {};
            querySnapshot.forEach(doc => {
                const pkg = doc.data();
                if (pkg.shipDate?.toDate) {
                    const dateObj = pkg.shipDate.toDate();
                    const year = dateObj.getFullYear();
                    const month = dateObj.getMonth(); // Mes de 0 a 11
                    if (!yearMonthMap[year]) {
                        yearMonthMap[year] = new Set();
                    }
                    yearMonthMap[year].add(month);
                }
            });

            const finalYears = Object.keys(yearMonthMap).sort((a, b) => b - a);
            const finalMonths = {};
            for (const year in yearMonthMap) {
                finalMonths[year] = Array.from(yearMonthMap[year]).sort((a, b) => a - b);
            }

            setAvailableYears(finalYears);
            setAvailableMonths(finalMonths);
        };

        fetchAvailableDates();
    }, []);

    // Efecto para cargar los paquetes del mes seleccionado
    useEffect(() => {
        if (!selectedYear || selectedMonth === '') {
            setPackages([]);
            return;
        }

        setIsLoading(true);
        const startDate = new Date(selectedYear, selectedMonth, 1);
        const endDate = new Date(selectedYear, parseInt(selectedMonth) + 1, 1);

        const packagesCollectionPath = `artifacts/${appId}/packages`;
        const q = query(
            collection(db, packagesCollectionPath),
            where('shipDate', '>=', startDate),
            where('shipDate', '<', endDate)
        );

        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            const packagesData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setPackages(packagesData);
            setIsLoading(false);
        }, (error) => {
            console.error("Error al cargar paquetes para validación:", error);
            showModal("Error", "No se pudieron cargar los paquetes para el mes seleccionado.");
            setIsLoading(false);
        });

        return () => unsubscribe();
    }, [selectedYear, selectedMonth, showModal]);


    // --- LÓGICA DE VALIDACIÓN (sin cambios) ---
    const checkMissingAuth = (pkg) => {
        const paymentNeedsAuth = ["transferencia", "depósito", "deposito", "link", "pos"];
        if (pkg.status && pkg.status.toLowerCase() === "entregado" &&
            pkg.paymentMethod && paymentNeedsAuth.includes(pkg.paymentMethod.toLowerCase()) &&
            (!pkg.authorizationCode || pkg.authorizationCode.trim() === "" || pkg.authorizationCode.toLowerCase() === "ignorar")) {
            if (pkg.authorizationCode && pkg.authorizationCode.toLowerCase() === "ignorar") return null;
            return "Falta autorización para pago electrónico.";
        }
        return null;
    };

    const checkMissingObservation = (pkg) => {
        if (pkg.status && pkg.status.toLowerCase() === "devolución" &&
            (!pkg.notes || pkg.notes.trim() === "" || pkg.notes.toLowerCase() === "ignorar")) {
            if (pkg.notes && pkg.notes.toLowerCase() === "ignorar") return null;
            return "Falta observación para devolución.";
        }
        return null;
    };

    const checkZeroValues = (pkg) => {
        if (pkg.orderId && pkg.orderId.toLowerCase().includes("dc")) {
            return null;
        }
        const productValue = parseFloat(pkg.productValue) || 0;
        const shippingCost = parseFloat(pkg.shippingCost) || 0;
        if (productValue === 0 && shippingCost === 0) return "Producto y Envío en Cero.";
        if (productValue === 0) return "Producto en Cero.";
        return null;
    };

    const generateErrorDetectionReport = () => {
        if (packages.length === 0) {
            showModal("Sin Datos", "Por favor, selecciona un año y mes con datos para generar el reporte.");
            return;
        }
        const errors = [];
        packages.forEach((pkg, index) => {
            let errorMessages = [];
            const missingAuthError = checkMissingAuth(pkg);
            if (missingAuthError) errorMessages.push({ type: 'auth', message: missingAuthError });

            const missingObservationError = checkMissingObservation(pkg);
            if (missingObservationError) errorMessages.push({ type: 'observation', message: missingObservationError });

            const zeroValueError = checkZeroValues(pkg);
            if (zeroValueError) errorMessages.push({type: 'zeroValue', message: zeroValueError});

            if (errorMessages.length > 0) {
                errors.push({ ...pkg, originalIndex: index + 1, errorDetail: errorMessages });
            }
        });
        setErrorReport(errors);
        setActiveReport('errors');
        if (errors.length === 0) {
            showModal("Errores Detectados", "No se encontraron errores con los criterios actuales en el mes seleccionado.");
        }
    };
    
    const handleUpdatePackage = async (packageId, updateData) => {
        const packageDocRef = doc(db, `artifacts/${appId}/packages`, packageId);
        await updateDoc(packageDocRef, { ...updateData, updatedAt: Timestamp.now() });
    };

    const handleIgnoreError = async (packageId, errorType) => {
        if (!canResolveErrors) {
            showModal("Permiso Denegado", "No tienes permiso para resolver errores.");
            return;
        }
        let updateData = {};
        if (errorType === 'auth') {
            updateData = { authorizationCode: "ignorar" };
        } else if (errorType === 'observation') {
            updateData = { notes: "ignorar" };
        } else {
            showModal("Acción no Soportada", "Esta acción no está implementada para este tipo de error.");
            return;
        }
        await handleUpdatePackage(packageId, updateData);
        showModal("Éxito", "Error marcado como ignorado.");
    };

    const generateDuplicateReport = () => {
        if (packages.length === 0) {
            showModal("Sin Datos", "Por favor, selecciona un año y mes con datos para generar el reporte.");
            return;
        }
        const orderIdCounts = {};
        packages.forEach(pkg => {
            if (pkg.orderId) {
                orderIdCounts[pkg.orderId] = (orderIdCounts[pkg.orderId] || 0) + 1;
            }
        });
        const duplicates = packages
            .map((pkg, index) => ({ ...pkg, originalIndex: index + 1, isDuplicate: (pkg.orderId && orderIdCounts[pkg.orderId] > 1) }))
            .filter(pkg => pkg.isDuplicate)
            .sort((a,b) => a.orderId.localeCompare(b.orderId) || (a.shipDate && b.shipDate ? a.shipDate.toDate().getTime() - b.shipDate.toDate().getTime() : 0));
        setDuplicateReport(duplicates);
        setActiveReport('duplicates');
        if (duplicates.length === 0) {
            showModal("Pedidos Duplicados", "No se encontraron pedidos duplicados en el mes seleccionado.");
        }
    };

    if (!permissions?.VALIDACIONES?.view) {
        return <div className="text-center py-10 bg-red-50 border border-red-200 rounded-lg shadow"><h2 className="text-2xl font-semibold text-red-700">Acceso Denegado</h2></div>;
    }

    return (
        <div className="bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-2xl font-semibold text-gray-800 mb-6">Validación de Errores y Repetidos</h2>
            
            <div className="mb-6 p-4 bg-gray-50 rounded-lg border">
                <h3 className="text-lg font-semibold text-gray-700 mb-3">Seleccionar Período a Validar</h3>
                <div className="flex items-center justify-start gap-4">
                    {/* --- CAMBIO #3: Filtro de Año ahora es dinámico --- */}
                    <div>
                        <label htmlFor="year-filter-val" className="font-medium text-gray-600 mr-2">Año:</label>
                        <select
                            id="year-filter-val"
                            value={selectedYear}
                            onChange={(e) => {
                                setSelectedYear(e.target.value);
                                setSelectedMonth('');
                            }}
                            className="input-field py-1 px-2 bg-white"
                        >
                            <option value="">Seleccione Año</option>
                            {availableYears.map(year => <option key={year} value={year}>{year}</option>)}
                        </select>
                    </div>
                    {/* --- CAMBIO #4: Filtro de Mes ahora es dinámico y depende del año --- */}
                    <div>
                        <label htmlFor="month-filter-val" className="font-medium text-gray-600 mr-2">Mes:</label>
                        <select
                            id="month-filter-val"
                            value={selectedMonth}
                            onChange={(e) => setSelectedMonth(e.target.value)}
                            className="input-field py-1 px-2 bg-white"
                            disabled={!selectedYear}
                        >
                            <option value="">Seleccione Mes</option>
                            {selectedYear && (availableMonths[selectedYear] || []).map(monthIndex => (
                                <option key={monthIndex} value={monthIndex}>
                                    {new Date(2000, monthIndex, 1).toLocaleString('es-GT', { month: 'long' })}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            <div className="flex space-x-4 mb-6">
                <button onClick={generateErrorDetectionReport} className="btn-primary">Generar Reporte de Errores</button>
                <button onClick={generateDuplicateReport} className="btn-primary">Generar Reporte Duplicados</button>
            </div>
            
            {isLoading && <div className="text-center py-10"><p className="animate-pulse">Cargando datos del mes...</p></div>}
            
            {!isLoading && packages.length === 0 && selectedYear && selectedMonth && (
                <div className="text-center py-10 bg-gray-50 rounded-lg"><p>No hay paquetes para el mes seleccionado.</p></div>
            )}
            {!isLoading && !activeReport && (!selectedYear || selectedMonth === '') && (
                <div className="text-center py-10 bg-sky-50 border border-sky-200 rounded-lg"><p className="text-sky-700 font-semibold">Por favor, selecciona un año y un mes para generar un reporte.</p></div>
            )}

            {activeReport === 'errors' && errorReport.length > 0 && (
                <div>
                    <h3 className="text-xl font-semibold text-gray-700 mb-3">Errores Detectados ({errorReport.length})</h3>
                    <div className="overflow-x-auto"><table className="min-w-full table-auto border-collapse border border-gray-300">
                        <thead className="bg-gray-100">
                            <tr>
                                {["No.", "Fecha Envío", "# Pedido", "Mensajero", "Estatus", "Error Detectado", "Acción"].map(h =>
                                    <th key={h} className="p-2 border border-gray-300 text-left text-sm font-semibold text-gray-600">{h}</th>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {errorReport.map((pkg, index) => (
                                <tr key={pkg.id || index} className="hover:bg-gray-50">
                                    <td className="p-2 border border-gray-300 text-sm">{index + 1}</td>
                                    <td className="p-2 border border-gray-300 text-sm">{formatDate(pkg.shipDate)}</td>
                                    <td className="p-2 border border-gray-300 text-sm font-medium text-sky-700">{pkg.orderId}</td>
                                    <td className="p-2 border border-gray-300 text-sm">{pkg.courierName}</td>
                                    <td className="p-2 border border-gray-300 text-sm">{pkg.status}</td>
                                    <td className="p-2 border border-gray-300 text-sm text-red-600">{pkg.errorDetail.map(err => err.message).join(", ")}</td>
                                    <td className="p-2 border border-gray-300 text-sm">
                                        {canResolveErrors && pkg.errorDetail.some(e => e.type === 'auth' || e.type === 'observation') &&
                                            <button onClick={() => handleIgnoreError(pkg.id, pkg.errorDetail[0].type)}
                                                className="text-green-600 hover:text-green-800 p-1 rounded hover:bg-green-100" title="Marcar como Ignorado">
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                                            </button>
                                        }
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table></div>
                </div>
            )}

            {activeReport === 'duplicates' && duplicateReport.length > 0 && (
                <div>
                    <h3 className="text-xl font-semibold text-gray-700 mb-3">Pedidos Duplicados ({duplicateReport.length})</h3>
                    <div className="overflow-x-auto"><table className="min-w-full table-auto border-collapse border border-gray-300">
                        <thead className="bg-gray-100">
                            <tr>
                                {["No.", "Fecha Envío", "# Pedido", "Mensajero", "Estatus"].map(h =>
                                    <th key={h} className="p-2 border border-gray-300 text-left text-sm font-semibold text-gray-600">{h}</th>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {duplicateReport.map((pkg, index) => (
                                <tr key={pkg.id || index} className="hover:bg-gray-50">
                                    <td className="p-2 border border-gray-300 text-sm">{index + 1}</td>
                                    <td className="p-2 border border-gray-300 text-sm">{formatDate(pkg.shipDate)}</td>
                                    <td className="p-2 border border-gray-300 text-sm font-medium text-sky-700">{pkg.orderId}</td>
                                    <td className="p-2 border border-gray-300 text-sm">{pkg.courierName}</td>
                                    <td className="p-2 border border-gray-300 text-sm">{pkg.status}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table></div>
                </div>
            )}
        </div>
    );
}

export default ValidacionesModule;